/**
 * The demo's email soft gate (SKE-4166, phase 3).
 *
 * Rendered as a sibling of `DemoResetButton` in Homepage, inside the existing
 * `isDemoMode()` guard, so the real reviewer path never mounts it.
 *
 * Three states, one component:
 *   LOCKED    a quiet bottom-left button offering personalization, plus the
 *             dialog that `useDemoGate` opens once on its own after a couple of
 *             actions. Closing it costs nothing (see useDemoGate).
 *   SUBMITTED the dialog shows the confirmation, not a spinner-then-nothing.
 *   UNLOCKED  `DemoPersonalizationSlot` — the phase-4 seam — replaces the
 *             button.
 *
 * Tailwind only (styled-components are lint-blocked in this app), and every
 * transition is `motion-safe:` so it disappears under
 * `prefers-reduced-motion: reduce`.
 */

import { useCallback, useState } from 'react'

import { Button } from 'ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'ui/dialog'
import { Input } from 'ui/input'
import { Label } from 'ui/label'

import { trackDemo } from './demoAnalytics'
import DemoPersonalizationSlot from './DemoPersonalizationSlot'
import {
  buildDemoLeadPayload,
  isLikelyEmail,
  submitDemoLead,
  type DemoLeadFailureReason,
} from './demoLead'
import { demoStrings } from './demoStrings'
import { markDemoUnlocked } from './demoUnlock'
import { useDemoGate } from './useDemoGate'
import { useDemoTurnstile } from './useDemoTurnstile'

const ERROR_STRINGS: Record<DemoLeadFailureReason, string> = {
  'not-configured': demoStrings.gateErrorServer,
  'invalid-email': demoStrings.gateErrorInvalidEmail,
  captcha: demoStrings.gateErrorCaptcha,
  'rate-limited': demoStrings.gateErrorRateLimited,
  network: demoStrings.gateErrorNetwork,
  server: demoStrings.gateErrorServer,
}

const DemoEmailGate: React.FC = () => {
  const gate = useDemoGate()
  const turnstile = useDemoTurnstile()

  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fail = useCallback(
    (reason: DemoLeadFailureReason, actionCount: number) => {
      setError(ERROR_STRINGS[reason])
      trackDemo('demo-email-failed', {
        reason,
        action_count: actionCount,
      })
    },
    []
  )

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()

      if (isSubmitting) {
        return
      }

      const candidate = email.trim()

      if (!isLikelyEmail(candidate)) {
        fail('invalid-email', gate.actionCount)
        return
      }

      // Turnstile only blocks when it is actually configured for this build;
      // where it isn't, the server decides whether a tokenless lead is allowed.
      if (turnstile.isEnabled && !turnstile.token) {
        fail('captcha', gate.actionCount)
        return
      }

      setError(null)
      setIsSubmitting(true)

      const result = await submitDemoLead(
        buildDemoLeadPayload({
          email: candidate,
          turnstileToken: turnstile.token || undefined,
          actionCount: gate.actionCount,
        })
      )

      setIsSubmitting(false)

      if (!result.ok) {
        // A single-use token may already have been spent at siteverify, so on
        // ANY server-side failure force a fresh challenge before a retry — a
        // reused token comes back captcha-failed and dead-ends the visitor.
        if (turnstile.isEnabled) {
          turnstile.reset()
        }
        fail(result.reason, gate.actionCount)
        return
      }

      // THE KPI. Fired only after the server accepted the lead (passed
      // validation + Turnstile), so it counts accepted leads, not raw form
      // submits. HubSpot/GA4 dispatch is best-effort server-side, so "accepted"
      // does not guarantee both side-effects landed.
      markDemoUnlocked(candidate)
      trackDemo('demo-email-submitted', {
        action_count: gate.actionCount,
        email_domain: candidate.split('@')[1]?.toLowerCase() ?? null,
      })
    },
    [email, fail, gate.actionCount, isSubmitting, turnstile]
  )

  if (!gate.isAvailable) {
    return null
  }

  return (
    <>
      {gate.isUnlocked ? (
        <DemoPersonalizationSlot />
      ) : (
        <button
          type="button"
          onClick={gate.open}
          aria-label={demoStrings.gateOpenAriaLabel}
          data-testid="demo-gate-open"
          // `max-[906px]:` is Homepage's own mobile breakpoint. Below it the
          // marketer mirror's role toggle owns bottom-left at z-[70], which
          // would otherwise sit directly on top of this button and swallow it —
          // stack rather than overlap. Desktop has no toggle and is unchanged.
          className="fixed bottom-4 left-4 z-50 rounded-full border border-blue-100 bg-blue-25 px-4 py-2 text-sm font-medium text-blue-600 shadow-sm hover:bg-blue-50 motion-safe:transition-colors max-[906px]:bottom-[4.25rem]"
        >
          {demoStrings.gateOpenLabel}
        </button>
      )}

      <Dialog
        open={gate.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            gate.dismiss()
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="demo-gate-dialog">
          {gate.isUnlocked ? (
            <>
              <DialogHeader>
                <DialogTitle>{demoStrings.gateUnlockedTitle}</DialogTitle>
                <DialogDescription>
                  {demoStrings.gateUnlockedGenericBody}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="button"
                  variant="blue-primary"
                  onClick={gate.dismiss}
                >
                  {demoStrings.gateDismissLabel}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <DialogHeader>
                <DialogTitle>{demoStrings.gateTitle}</DialogTitle>
                <DialogDescription>{demoStrings.gateBody}</DialogDescription>
              </DialogHeader>

              <div className="mt-4 flex flex-col gap-2">
                <Label htmlFor="demo-gate-email">
                  {demoStrings.gateEmailLabel}
                </Label>
                <Input
                  id="demo-gate-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder={demoStrings.gateEmailPlaceholder}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value)
                    setError(null)
                  }}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'demo-gate-error' : undefined}
                />

                {/* Container is only in the tree when a site key exists; the
                    hook's callback ref renders the widget into it. */}
                {turnstile.isEnabled ? (
                  <div ref={turnstile.turnstileRef} className="mt-1" />
                ) : null}

                {error ? (
                  <p
                    id="demo-gate-error"
                    role="alert"
                    className="text-sm text-red-600"
                  >
                    {error}
                  </p>
                ) : null}

                <p className="text-xs text-neutral-500">
                  {demoStrings.gateFootnote}
                </p>
              </div>

              <DialogFooter className="mt-4">
                <Button type="button" variant="ghost" onClick={gate.dismiss}>
                  {demoStrings.gateDismissLabel}
                </Button>
                <Button
                  type="submit"
                  variant="blue-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? demoStrings.gateSubmittingLabel
                    : demoStrings.gateSubmitLabel}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default DemoEmailGate
